/**
 * GeoTileMap — the OpenStreetMap basemap the geographic Map View draws its cameras on.
 *
 * A thin, opinionated wrapper around leaflet that exists to do four things the board should
 * not have to know about:
 *
 *  1. LOAD LEAFLET ONLY WHEN THIS BOARD IS OPENED. The library and its stylesheet are pulled
 *     in with a dynamic `import()`, so the ~40 KB never lands in the bundle an operator
 *     downloads to watch the honeycomb.
 *
 *  2. SAY WHEN THERE IS NO BASEMAP TO DRAW. No tiles ship with the product — bundling a tile
 *     pack would add hundreds of megabytes to every deployment for a feature many sites never
 *     open — so the basemap comes off the network. On an isolated node that network isn't
 *     there, and a blank grey grid reads as "broken product". This probes one tile up front
 *     and watches leaflet's own `tileerror` afterwards, then says plainly that the basemap is
 *     unreachable and offers a retry. Cameras are never hidden by it: the pins still draw,
 *     on a plain background, because knowing WHICH camera is alerting still matters when you
 *     can't see the street it's on.
 *
 *  3. PROJECT for React. Markers are rendered by the caller, not by leaflet — that is what
 *     lets this board reuse the floor plan's `MapMarker`, preview card and alert popover
 *     unchanged. Two overlay layers are offered:
 *       · `pins`    — positioned in LAYER space. Leaflet translates the whole layer during a
 *                     pan, so a drag costs one style write, not a React render per frame.
 *       · `overlay` — positioned in CONTAINER space (screen pixels), for the floating preview
 *                     card and hover popover, which need the viewport box to decide which
 *                     side of the pin to open on.
 *
 *  4. REPORT THE VIEW. Every settled move/zoom is handed back, which is what the board saves
 *     as the deployment default and remembers per browser.
 *
 * Zoom animation is off on purpose: with it on, leaflet reports an intermediate view for the
 * duration of the animation and the React-rendered pins slide away from the map under them.
 */
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import type * as LeafletNS from 'leaflet';

/** A point in the map's own pixel space (layer) or in the container's (screen). */
export interface GeoPoint {
  x: number;
  y: number;
}

export interface GeoView {
  lat: number;
  lng: number;
  zoom: number;
}

export interface GeoTileMapHandle {
  /** Move the view without a page round-trip — "go to the saved default", "frame the pins". */
  setView(view: GeoView): void;
  /** Frame every supplied coordinate. No-op for an empty list. */
  fitTo(points: Array<{ lat: number; lng: number }>): void;
  zoomBy(delta: number): void;
  /** The view as it stands right now — what the "set as default" action saves. */
  current(): GeoView | null;
  /**
   * A viewport pixel (a pointer event's clientX/clientY) back to a coordinate — what a pin
   * drag needs. It goes through leaflet's own container box rather than the caller's, because
   * the pin layer is translated during a pan and its offsets cannot be trusted mid-drag.
   */
  clientToLatLng(clientX: number, clientY: number): { lat: number; lng: number } | null;
}

/** How long a probe tile has to load before the basemap is called unreachable. A LAN with no
 *  route out usually fails fast, but a captive portal or a slow uplink can hang. */
const PROBE_TIMEOUT_MS = 6000;

/** Consecutive tile failures before an initially-fine basemap is declared gone (a couple of
 *  missing tiles at the edge of coverage is normal and must not blank the map). */
const TILE_ERROR_LIMIT = 8;

export interface GeoTileMapProps {
  tileUrl: string;
  attribution: string;
  /** Where the map opens. Changing it does NOT move a map already on screen — the operator's
   *  current position is theirs; use the handle to move deliberately. */
  initialView: GeoView;
  /** Fired once the view settles (moveend/zoomend), never mid-drag. */
  onViewChange?: (view: GeoView) => void;
  /** A click on the basemap itself (not on a pin). Used to place an armed camera. */
  onMapClick?: (lat: number, lng: number) => void;
  /** Crosshair cursor while a camera is armed for placement. */
  placing?: boolean;
  /**
   * Keep the SCREEN-space overlay glued to its pin during a pan.
   *
   * Off by default: the overlay's position has to be recomputed in React, and doing that on
   * every frame of every drag would cost a render per frame even with no card open. The
   * board turns it on only while a preview or popover is actually showing.
   */
  trackOverlay?: boolean;
  /** Layer-space children: re-rendered when the projection changes, translated by leaflet
   *  during a pan. `project` maps a coordinate to layer pixels. */
  pins?: (project: (lat: number, lng: number) => GeoPoint, zoom: number) => ReactNode;
  /** Screen-space children (preview card, popover). `project` maps to container pixels, and
   *  `size` is the map's box, for deciding which way a card should open. */
  overlay?: (
    project: (lat: number, lng: number) => GeoPoint,
    size: { w: number; h: number },
  ) => ReactNode;
  className?: string;
  handleRef?: Ref<GeoTileMapHandle>;
}

type BasemapState = 'loading' | 'ok' | 'unreachable';

/** Swap a tile template's placeholders for one concrete tile, to test the source. */
function probeUrl(template: string): string {
  return template
    .replace(/\{s\}/g, 'a')
    .replace(/\{z\}/g, '0')
    .replace(/\{x\}/g, '0')
    .replace(/\{y\}/g, '0')
    .replace(/\{r\}/g, '');
}

export default function GeoTileMap({
  tileUrl,
  attribution,
  initialView,
  onViewChange,
  onMapClick,
  placing,
  pins,
  overlay,
  trackOverlay = false,
  className = '',
  handleRef,
}: GeoTileMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletNS.Map | null>(null);
  const LRef = useRef<typeof LeafletNS | null>(null);
  const tileErrorsRef = useRef(0);

  const [ready, setReady] = useState(false);
  const [basemap, setBasemap] = useState<BasemapState>('loading');
  // Bumped whenever the PROJECTION changes (zoom, resize, pane reset) — not on every pan
  // frame, which is handled by translating the layer instead.
  const [projectionTick, setProjectionTick] = useState(0);
  // Bumped on every settled move as well, so the screen-space overlay (one card at most)
  // tracks the pin it is anchored to.
  const [screenTick, setScreenTick] = useState(0);
  const [zoom, setZoom] = useState(initialView.zoom);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // Probe counter: bumping it re-runs the reachability check behind the Retry button.
  const [probeAttempt, setProbeAttempt] = useState(0);

  // The latest callbacks, so the leaflet listeners can be bound once for the map's life
  // instead of being torn down and rebound every time the board re-renders.
  const onViewChangeRef = useRef(onViewChange);
  const onMapClickRef = useRef(onMapClick);
  const trackOverlayRef = useRef(trackOverlay);
  const overlayRafRef = useRef<number | null>(null);
  onViewChangeRef.current = onViewChange;
  onMapClickRef.current = onMapClick;
  trackOverlayRef.current = trackOverlay;

  // ── Is there a basemap at all? ────────────────────────────────────────────
  //
  // Runs before/beside the map rather than waiting for leaflet's own errors, so an isolated
  // node says "no internet" immediately instead of after a screen of failed tile requests.
  useEffect(() => {
    let cancelled = false;
    setBasemap('loading');
    tileErrorsRef.current = 0;

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setBasemap('unreachable');
      return () => {
        cancelled = true;
      };
    }

    const img = new Image();
    const timer = setTimeout(() => {
      if (!cancelled) {
        img.src = '';
        setBasemap('unreachable');
      }
    }, PROBE_TIMEOUT_MS);
    img.onload = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setBasemap('ok');
    };
    img.onerror = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setBasemap('unreachable');
    };
    // Cross-origin without credentials: a tile is a public image, and asking for credentials
    // would fail CORS on most tile servers.
    img.crossOrigin = 'anonymous';
    img.src = probeUrl(tileUrl);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tileUrl, probeAttempt]);

  // A node that comes back online should get its basemap back without a reload.
  useEffect(() => {
    const retry = () => setProbeAttempt((n) => n + 1);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  // ── The map itself ────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    let map: LeafletNS.Map | null = null;
    // Ours, not leaflet's, so `map.remove()` does not take it with it — see WHEEL ZOOM.
    let wheelHost: HTMLElement | null = null;
    let onWheel: ((ev: WheelEvent) => void) | null = null;

    (async () => {
      // Dynamic, so leaflet and its stylesheet stay out of the main bundle — this board is
      // one of three on the page and the other two never need them.
      const [leaflet] = await Promise.all([
        import('leaflet'),
        // @ts-ignore — a side-effect CSS import, resolved by the app's bundler. Not
        // `@ts-expect-error`: the apps pull in vite/client, which DOES declare `*.css`,
        // so there the directive would be unused and TS2578 would fail their typecheck.
        import('leaflet/dist/leaflet.css'),
      ]);
      if (cancelled || !hostRef.current) return;
      const L = (leaflet as any).default ?? leaflet;
      LRef.current = L;

      const m: LeafletNS.Map = L.map(hostRef.current, {
        center: [initialView.lat, initialView.lng],
        zoom: initialView.zoom,
        // See the file header: animated zoom reports an intermediate view and the
        // React-rendered pins drift away from the map beneath them.
        zoomAnimation: false,
        // The default control sits top-left, exactly where the board's own chrome goes.
        zoomControl: false,
        attributionControl: true,
        worldCopyJump: true,
        // Replaced by the stepped handler below — see WHEEL ZOOM.
        scrollWheelZoom: false,
      });
      map = m;
      mapRef.current = m;

      const settle = () => {
        const c = m.getCenter();
        setZoom(m.getZoom());
        setProjectionTick((n) => n + 1);
        setScreenTick((n) => n + 1);
        onViewChangeRef.current?.({ lat: c.lat, lng: c.lng, zoom: m.getZoom() });
      };

      // Pan: translate the pin layer by the map pane's own offset. One style write per
      // frame, no React render — the same trick leaflet uses for its own marker pane.
      const follow = () => {
        const pane = m.getPanes().mapPane;
        const pos = L.DomUtil.getPosition(pane);
        if (layerRef.current && pos) {
          layerRef.current.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
        }
        // The floating card can't ride the transform — it is anchored in screen space — so
        // it is re-projected in React, at most once per frame, and only while one is open.
        if (trackOverlayRef.current && overlayRafRef.current == null) {
          overlayRafRef.current = requestAnimationFrame(() => {
            overlayRafRef.current = null;
            setScreenTick((n) => n + 1);
          });
        }
      };

      m.on('move', follow);
      // Zoom and pane resets change the projection itself, so the pins must be recomputed.
      m.on('zoomend viewreset resize', () => {
        follow();
        setProjectionTick((n) => n + 1);
        setZoom(m.getZoom());
        setSize({ w: m.getSize().x, h: m.getSize().y });
      });
      m.on('moveend', settle);
      m.on('click', (e: LeafletNS.LeafletMouseEvent) => {
        onMapClickRef.current?.(e.latlng.lat, e.latlng.lng);
      });
      /**
       * WHEEL ZOOM — one level per gesture, anchored under the pointer.
       *
       * leaflet's own handler accumulates wheel delta and converts the total into up to FOUR
       * zoom levels per burst. A mouse notch is one event and behaves; a trackpad flick is
       * dozens of events in a few hundred milliseconds, so a light swipe crosses half the
       * country and the operator has lost the camera they were looking at. Zoom is also the
       * expensive gesture here — every level re-projects every pin — so overshooting costs a
       * wasted render on the way out and another on the way back.
       *
       * So the built-in is off and a wheel is a STEP: the same ±1 the toolbar buttons apply,
       * just centred on the cursor rather than the map. Two guards, because a wheel is not
       * one kind of input:
       *   QUIET — a flick and its momentum are ONE gesture. After the first step, the stream
       *           is ignored until it has gone quiet, so the tail costs nothing.
       *   HOLD  — except that someone scrolling continuously does mean "keep going", and a
       *           map that stopped responding would read as broken. So a held scroll still
       *           steps, at a readable pace, rather than not at all.
       */
      const WHEEL_QUIET_MS = 140;
      const WHEEL_HOLD_MS = 400;
      let lastWheelAt = 0;
      let lastStepAt = 0;
      wheelHost = hostRef.current;
      onWheel = (ev: WheelEvent) => {
        // The board must not scroll away behind the map while the pointer is over it.
        ev.preventDefault();
        if (!ev.deltaY) return;
        const now = Date.now();
        const newGesture = now - lastWheelAt >= WHEEL_QUIET_MS;
        lastWheelAt = now;
        if (!newGesture && now - lastStepAt < WHEEL_HOLD_MS) return;
        lastStepAt = now;
        // setZoomAround, not setZoom: the point under the cursor stays under the cursor,
        // which is what makes "zoom into that junction" one gesture instead of zoom-then-pan.
        m.setZoomAround(m.mouseEventToLatLng(ev), m.getZoom() + (ev.deltaY < 0 ? 1 : -1));
      };
      wheelHost.addEventListener('wheel', onWheel, { passive: false });

      // Enough failures and the source is gone, not just thin at this zoom.
      m.on('tileerror', () => {
        tileErrorsRef.current += 1;
        if (tileErrorsRef.current >= TILE_ERROR_LIMIT) setBasemap('unreachable');
      });

      follow();
      setSize({ w: m.getSize().x, h: m.getSize().y });
      setReady(true);
    })();

    return () => {
      cancelled = true;
      if (overlayRafRef.current != null) cancelAnimationFrame(overlayRafRef.current);
      if (wheelHost && onWheel) wheelHost.removeEventListener('wheel', onWheel);
      map?.remove();
      mapRef.current = null;
      setReady(false);
    };
    // Created ONCE. `initialView` is a starting position, not a binding — re-creating the
    // map when it changes would yank an operator back to the default mid-investigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The tile layer is separate from the map so a config change (a site pointing at its own
  // tile server) swaps the basemap without tearing down the view the operator is on.
  useEffect(() => {
    const L = LRef.current;
    const map = mapRef.current;
    if (!L || !map || basemap !== 'ok') return;
    tileErrorsRef.current = 0;
    const layer = L.tileLayer(tileUrl, {
      attribution,
      maxZoom: 19,
      // Tiles the source doesn't have at this zoom are upscaled from the last one that
      // exists, rather than leaving holes over the area the operator zoomed into.
      maxNativeZoom: 19,
    });
    layer.addTo(map);
    return () => {
      layer.remove();
    };
  }, [tileUrl, attribution, basemap, ready]);

  // Leaflet must be told when its box changes — it caches the size, and a stale one puts
  // every projection (and therefore every pin) at the wrong offset.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || !ready) return;
    const ro = new ResizeObserver(() => {
      mapRef.current?.invalidateSize();
      const s = mapRef.current?.getSize();
      if (s) setSize({ w: s.x, h: s.y });
      setProjectionTick((n) => n + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready]);

  useImperativeHandle(
    handleRef,
    (): GeoTileMapHandle => ({
      setView: (v) => mapRef.current?.setView([v.lat, v.lng], v.zoom),
      fitTo: (points) => {
        const L = LRef.current;
        const map = mapRef.current;
        if (!L || !map || !points.length) return;
        map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number])), {
          padding: [48, 48],
          maxZoom: 18,
        });
      },
      zoomBy: (delta) => mapRef.current?.setZoom((mapRef.current?.getZoom() ?? 0) + delta),
      current: () => {
        const map = mapRef.current;
        if (!map) return null;
        const c = map.getCenter();
        return { lat: c.lat, lng: c.lng, zoom: map.getZoom() };
      },
      clientToLatLng: (clientX, clientY) => {
        const map = mapRef.current;
        const el = hostRef.current;
        if (!map || !el) return null;
        const r = el.getBoundingClientRect();
        const ll = map.containerPointToLatLng([clientX - r.left, clientY - r.top]);
        return { lat: ll.lat, lng: ll.lng };
      },
    }),
    [],
  );

  /** Coordinate → layer pixels. Recomputed only when the projection changes. */
  const projectLayer = useCallback(
    (lat: number, lng: number): GeoPoint => {
      const map = mapRef.current;
      if (!map) return { x: 0, y: 0 };
      const p = map.latLngToLayerPoint([lat, lng]);
      return { x: p.x, y: p.y };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectionTick, ready],
  );

  /** Coordinate → container (screen) pixels, for the floating cards. */
  const projectContainer = useCallback(
    (lat: number, lng: number): GeoPoint => {
      const map = mapRef.current;
      if (!map) return { x: 0, y: 0 };
      const p = map.latLngToContainerPoint([lat, lng]);
      return { x: p.x, y: p.y };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screenTick, projectionTick, ready],
  );

  const pinLayer = useMemo(
    () => (ready && pins ? pins(projectLayer, zoom) : null),
    [ready, pins, projectLayer, zoom],
  );

  return (
    /**
     * `isolate` — THE MAP'S Z-INDEXES MUST NOT LEAVE THE MAP.
     *
     * Leaflet stacks its own world in the hundreds: panes at 200-400, controls at 1000, popups
     * at 700. This box was `relative` with no z-index of its own, which positions children but
     * creates NO stacking context — so all of that competed in whatever ancestor context the
     * board happened to sit in, at numbers no layout in this app uses.
     *
     * It won everything. Opening an event record over the Search page's tracking panel drew the
     * basemap straight through the modal (`z-50`) and its backdrop: half a record, with a map of
     * Mumbai across the bottom of it.
     *
     * `isolation: isolate` confines every one of those numbers to this element without changing
     * how anything inside it paints. The map then takes its place in the page like any other
     * box, and a modal at `z-50` is above it because 50 is above auto. Anything that must appear
     * OVER the map is already rendered inside it (the pin layer, the preview card, the basemap
     * notice) and is unaffected.
     */
    <div className={`relative isolate h-full w-full ${className}`}>
      <div
        ref={hostRef}
        className="absolute inset-0"
        style={{
          // Leaflet paints its own light background; on a dark theme an unpainted map box
          // would flash white before the first tile lands.
          background: basemap === 'ok' ? undefined : 'transparent',
          cursor: placing ? 'crosshair' : undefined,
        }}
      />

      {/* Pins. Sits above leaflet's panes and moves with them; pointer events are off on the
          layer itself so a drag that starts between pins still pans the map. */}
      <div
        ref={layerRef}
        className="pointer-events-none absolute left-0 top-0 z-[500]"
        style={{ width: 0, height: 0 }}
      >
        {/* Children re-enable pointer events on themselves — a pin is clickable, the empty space
            around it is the map. */}
        <div className="pointer-events-auto">{pinLayer}</div>
      </div>

      {/* Screen-space cards. Above the pins, and non-interactive except where a card opts in. */}
      {ready && overlay && (
        <div className="pointer-events-none absolute inset-0 z-[600]">
          {overlay(projectContainer, size)}
        </div>
      )}

      {basemap !== 'ok' && (
        <div className="pointer-events-none absolute inset-0 z-[400] flex items-start justify-center pt-6">
          <div className="pointer-events-auto rounded-lg border border-border bg-card/95 px-4 py-3 text-center shadow-card backdrop-blur-sm">
            {basemap === 'loading' ? (
              <p className="text-xs text-muted-foreground">Checking for a basemap…</p>
            ) : (
              <>
                <p className="text-xs font-semibold text-foreground">Basemap unavailable</p>
                <p className="mt-1 max-w-[42ch] text-[11px] leading-relaxed text-muted-foreground">
                  Map tiles could not be reached — this node has no route to the tile service.
                  Camera positions and their alerts are still shown below. Point the map at a
                  tile server on your own network to get the basemap back.
                </p>
                <button
                  type="button"
                  onClick={() => setProbeAttempt((n) => n + 1)}
                  className="mt-2 rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Try again
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
